/**
 * ConfirmModal.js — the app's own yes/no.
 *
 * Until now the destructive moments left the app to ask: window.confirm() gave
 * the browser's chrome — a white system strip at the top of the page, unstyled,
 * untranslatable in some places, and on a phone the least tappable control in
 * the whole workspace. Worse, it said "Delete X?" and nothing about what losing
 * X means.
 *
 * This keeps the question inside the workspace, in the app's language and
 * colours, with thumb-sized buttons, and makes the CONSEQUENCE the body of the
 * message: a scenario cannot be undone, so the button says Delete scenario and
 * the copy says it is gone for good.
 *
 * Props:
 * - title:    string
 * - message:  string        what is at stake, in one sentence
 * - confirmLabel: string    the verb, never "OK"
 * - danger:   bool          red confirm button for irreversible actions
 * - onConfirm, onCancel: () => void
 */
import React, { useEffect, useRef } from 'react';
import { useI18n } from './i18n';

export default function ConfirmModal({ title = '', message = '', confirmLabel = 'Confirm', danger = false, onConfirm, onCancel }) {
  const { t } = useI18n();
  const confirmRef = useRef(null);
  useEffect(() => { if (confirmRef.current) confirmRef.current.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel && onCancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const btn = (primary) => ({
    minWidth: 120, minHeight: 40, padding: '0 14px', borderRadius: 8, cursor: 'pointer',
    fontSize: '0.86rem', fontWeight: 700, border: '1px solid transparent',
    background: primary ? (danger ? '#dc2626' : '#4f46e5') : '#334155',
    color: '#fff',
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        // The confirm may sit ON TOP of another modal whose backdrop also
        // closes on a click. Answering "no" must not also answer the question
        // underneath it.
        e.stopPropagation();
        if (e.target === e.currentTarget) onCancel && onCancel();
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(2, 6, 23, 0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div style={{
        width: 'min(420px, 100%)', background: '#0f172a', border: '1px solid #334155',
        borderRadius: 12, padding: '1rem 1.1rem', color: '#e2e8f0',
        boxShadow: '0 18px 44px rgba(0,0,0,0.6)',
      }}>
        <h2 style={{ margin: '0 0 6px', fontSize: '1rem' }}>{title}</h2>
        <p style={{ margin: '0 0 14px', fontSize: '0.84rem', color: '#94a3b8', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" style={btn(false)} onClick={() => onCancel && onCancel()}>{t('confirm.cancel')}</button>
          <button ref={confirmRef} type="button" style={btn(true)} onClick={() => onConfirm && onConfirm()}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
